/**
 * Budget Utilization Progress Bar
 *
 * Visual progress bar showing budget utilization with threshold marker
 * Uses TailwindCSS for styling
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { HiExclamationCircle, HiCheckCircle } from 'react-icons/hi';

export interface BudgetUtilizationBarProps {
  currentSpend: number;
  monthlyBudget: number;
  utilizationPercentage: number;
  alertThreshold: number;
  alertTriggered: boolean;
}

export const BudgetUtilizationBar: React.FC<BudgetUtilizationBarProps> = ({
  currentSpend,
  monthlyBudget,
  utilizationPercentage,
  alertThreshold,
  alertTriggered
}) => {
  // Determine color based on utilization
  const getColor = () => {
    if (utilizationPercentage >= 100) return 'bg-red-600';
    if (alertTriggered) return 'bg-amber-500';
    return 'bg-blue-600';
  };

  const getTextColor = () => {
    if (utilizationPercentage >= 100) return 'text-red-600';
    if (alertTriggered) return 'text-amber-600';
    return 'text-blue-600';
  };

  // Cap percentage at 100 for visual display
  const displayPercentage = Math.min(utilizationPercentage, 100);

  return (
    <div className="space-y-3">
      {/* Header with current spend and budget */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium text-gray-700">Budget Utilization</p>
          <p className="text-xs text-gray-500 mt-0.5">
            ${currentSpend.toFixed(2)} of ${monthlyBudget.toFixed(2)}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {alertTriggered ? (
            <>
              <HiExclamationCircle className="w-5 h-5 text-amber-500" />
              <span className={`text-lg font-bold ${getTextColor()}`}>
                {utilizationPercentage.toFixed(1)}%
              </span>
            </>
          ) : (
            <>
              <HiCheckCircle className="w-5 h-5 text-green-500" />
              <span className={`text-lg font-bold ${getTextColor()}`}>
                {utilizationPercentage.toFixed(1)}%
              </span>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
          <div
            className={`h-6 ${getColor()} transition-all duration-300 rounded-full flex items-center justify-end pr-2`}
            style={{ width: `${displayPercentage}%` }}
          >
            {displayPercentage > 10 && (
              <span className="text-xs font-medium text-white">
                {utilizationPercentage.toFixed(1)}%
              </span>
            )}
          </div>
        </div>

        {/* Alert threshold marker */}
        {alertThreshold > 0 && alertThreshold < 100 && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-amber-400"
            style={{ left: `${alertThreshold}%` }}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <div className="flex flex-col items-center">
                <span className="text-xs text-amber-600 font-medium">
                  Alert: {alertThreshold}%
                </span>
                <div className="w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-amber-400"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status message */}
      <div className="flex justify-between items-center text-xs">
        <div className="flex items-center space-x-1">
          {utilizationPercentage >= 100 ? (
            <span className="text-red-600 font-medium">Budget Exceeded</span>
          ) : alertTriggered ? (
            <span className="text-amber-600 font-medium">
              Approaching Limit (Alert at {alertThreshold}%)
            </span>
          ) : (
            <span className="text-green-600 font-medium">On Track</span>
          )}
        </div>
        <div className="text-gray-500">
          ${(monthlyBudget - currentSpend).toFixed(2)} remaining
        </div>
      </div>

      {/* Over-budget warning */}
      {utilizationPercentage > 100 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
          <div className="flex items-start space-x-2">
            <HiExclamationCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Budget Exceeded</p>
              <p className="text-xs text-red-700 mt-1">
                Project has exceeded the monthly budget by ${(currentSpend - monthlyBudget).toFixed(2)}.
                Consider adjusting the budget or reviewing project costs.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetUtilizationBar;
